import React, { useState } from 'react';
import Button from '../Button/Button';
import styles from './CompetitionOverviewEditor.module.css';

/**
 * Editor for the competition details students read at
 * /student/competitions/:templateId.
 *
 * Used two ways from the Competition Setup page: straight after a guide is
 * parsed (so the admin can correct what was read while the document is still in
 * front of them), and any time afterwards. It also builds details from nothing,
 * for a competition with no guide to upload.
 *
 * The draft is held in the student page's own shape and sent as one object —
 * the backend normalizes it exactly as it normalizes a parsed guide, so a date
 * typed here is validated like a date the model read. Two fields are absent on
 * purpose: which stage is current and which deadline is next are computed from
 * the stage dates server-side, so they cannot drift from the dates below.
 */

const blankStage = () => ({
  name: '',
  deadline: { date: '' },
  description: '',
  includes: [],
  deliverables: [],
});

const blankDeliverable = () => ({ title: '', requirement: '', note: '' });

/** The draft always has every section present, so the inputs stay controlled. */
const toDraft = (overview) => ({
  name: overview?.name || '',
  tagline: overview?.tagline || '',
  focus: overview?.focus || '',
  participation: overview?.participation || '',
  eligibility: overview?.eligibility || '',
  officialWebsite: { url: overview?.officialWebsite?.url || '' },
  stages: (overview?.stages || []).map((s) => ({
    name: s.name || '',
    deadline: { date: s.deadline?.date || '' },
    description: s.description || '',
    includes: s.includes || [],
    deliverables: (s.deliverables || []).map((d) => ({
      title: d.title || '',
      requirement: d.requirement || '',
      note: d.note || '',
    })),
  })),
  rules: (overview?.rules || []).map((r) => ({ label: r.label || '', text: r.text || '' })),
  links: (overview?.links || []).map((l) => ({ label: l.label || '', url: l.url || '' })),
  roadmap: {
    now: { title: overview?.roadmap?.now?.title || '', text: overview?.roadmap?.now?.text || '' },
    afterReview: {
      title: overview?.roadmap?.afterReview?.title || '',
      text: overview?.roadmap?.afterReview?.text || '',
    },
    then: { title: overview?.roadmap?.then?.title || '', text: overview?.roadmap?.then?.text || '' },
  },
});

const CompetitionOverviewEditor = ({ initial, busy, onSave, onCancel }) => {
  const [draft, setDraft] = useState(() => toDraft(initial));

  const setField = (key) => (e) => setDraft((d) => ({ ...d, [key]: e.target.value }));

  /** Replace one entry of a repeatable list (stages, rules, links). */
  const setRow = (listKey, index, patch) =>
    setDraft((d) => ({
      ...d,
      [listKey]: d[listKey].map((row, i) => (i === index ? { ...row, ...patch } : row)),
    }));

  const addRow = (listKey, blank) =>
    setDraft((d) => ({ ...d, [listKey]: [...d[listKey], blank] }));

  const removeRow = (listKey, index) =>
    setDraft((d) => ({ ...d, [listKey]: d[listKey].filter((_, i) => i !== index) }));

  const setStageDeliverables = (stageIndex, deliverables) =>
    setRow('stages', stageIndex, { deliverables });

  const setRoadmap = (slot, key) => (e) =>
    setDraft((d) => ({
      ...d,
      roadmap: { ...d.roadmap, [slot]: { ...d.roadmap[slot], [key]: e.target.value } },
    }));

  const roadmapSlots = [
    ['now', 'Now', 'What the student should be doing for the stage that is open.'],
    ['afterReview', 'After mentor review', 'What happens once their mentor approves the work.'],
    ['then', 'Then', 'What comes after, if the competition has later stages.'],
  ];

  return (
    <div className={styles.editor}>
      <p className={styles.note}>
        Everything saved here is live for students immediately. “You are preparing …”
        and the next official deadline are worked out from the stage deadlines below,
        so there is nothing to set for those.
      </p>

      {/* ---- The fact tiles at the top of the student page ---- */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>About the competition</h3>
        <label className={styles.field}>Name
          <input value={draft.name} onChange={setField('name')} placeholder="Conrad Challenge" />
          <span className={styles.hint}>Leave blank to use the competition's own name.</span>
        </label>
        <label className={styles.field}>Tagline
          <textarea rows={2} value={draft.tagline} onChange={setField('tagline')} />
        </label>
        <div className={styles.row}>
          <label className={styles.field}>Focus
            <input value={draft.focus} onChange={setField('focus')} placeholder="Innovation & entrepreneurship" />
          </label>
          <label className={styles.field}>Participation
            <input value={draft.participation} onChange={setField('participation')} placeholder="Team-based" />
          </label>
        </div>
        <div className={styles.row}>
          <label className={styles.field}>Eligibility
            <input value={draft.eligibility} onChange={setField('eligibility')} placeholder="Grades 8–12" />
          </label>
          <label className={styles.field}>Official website
            <input
              value={draft.officialWebsite.url}
              onChange={(e) => setDraft((d) => ({ ...d, officialWebsite: { url: e.target.value } }))}
              placeholder="https://..."
            />
            <span className={styles.hint}>Must start with http:// or https://.</span>
          </label>
        </div>
      </section>

      {/* ---- Stages, each with its own deliverables ---- */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Stages ({draft.stages.length})</h3>
        <p className={styles.hint}>
          The competition's rounds, in the order they happen. A stage with no deadline
          still shows — it just says the date has not been added.
        </p>
        {draft.stages.map((stage, i) => (
          <div key={i} className={styles.card}>
            <div className={styles.cardHead}>
              <span className={styles.cardTitle}>Stage {i + 1}</span>
              <button type="button" className={styles.dangerBtn} onClick={() => removeRow('stages', i)}>
                Remove stage
              </button>
            </div>
            <div className={styles.row}>
              <label className={styles.field}>Stage name
                <input
                  value={stage.name}
                  onChange={(e) => setRow('stages', i, { name: e.target.value })}
                  placeholder="Activation Stage"
                />
              </label>
              <label className={styles.field}>Deadline
                <input
                  type="date"
                  value={stage.deadline.date}
                  onChange={(e) => setRow('stages', i, { deadline: { date: e.target.value } })}
                />
              </label>
            </div>
            <label className={styles.field}>What happens in this stage
              <textarea
                rows={2}
                value={stage.description}
                onChange={(e) => setRow('stages', i, { description: e.target.value })}
              />
            </label>
            <label className={styles.field}>Includes
              <input
                value={stage.includes.join(', ')}
                onChange={(e) =>
                  setRow('stages', i, {
                    includes: e.target.value.split(',').map((x) => x.trim()).filter(Boolean),
                  })
                }
                placeholder="written, video, presentation"
              />
              <span className={styles.hint}>Comma separated — the kinds of material asked for.</span>
            </label>

            <div className={styles.subList}>
              <span className={styles.subTitle}>Deliverables</span>
              {stage.deliverables.map((d, j) => (
                <div key={j} className={styles.deliverable}>
                  <input
                    value={d.title}
                    onChange={(e) =>
                      setStageDeliverables(
                        i,
                        stage.deliverables.map((x, k) => (k === j ? { ...x, title: e.target.value } : x))
                      )
                    }
                    placeholder="Pitch video"
                  />
                  <input
                    value={d.requirement}
                    onChange={(e) =>
                      setStageDeliverables(
                        i,
                        stage.deliverables.map((x, k) => (k === j ? { ...x, requirement: e.target.value } : x))
                      )
                    }
                    placeholder="2 minutes max"
                  />
                  <input
                    value={d.note}
                    onChange={(e) =>
                      setStageDeliverables(
                        i,
                        stage.deliverables.map((x, k) => (k === j ? { ...x, note: e.target.value } : x))
                      )
                    }
                    placeholder="What it must contain"
                  />
                  <button
                    type="button"
                    className={styles.dangerBtn}
                    onClick={() =>
                      setStageDeliverables(i, stage.deliverables.filter((_, k) => k !== j))
                    }
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                className={styles.linkBtn}
                onClick={() => setStageDeliverables(i, [...stage.deliverables, blankDeliverable()])}
              >
                + Add deliverable
              </button>
              <span className={styles.hint}>
                A deliverable with no title is dropped. The limit and note are optional.
              </span>
            </div>
          </div>
        ))}
        <button type="button" className={styles.linkBtn} onClick={() => addRow('stages', blankStage())}>
          + Add stage
        </button>
      </section>

      {/* ---- Key rules ---- */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Key rules ({draft.rules.length})</h3>
        <p className={styles.hint}>
          Conditions a student has to meet or follow. A rule needs both a label and
          the rule itself, or it is dropped.
        </p>
        {draft.rules.map((rule, i) => (
          <div key={i} className={styles.pair}>
            <input
              className={styles.pairLabel}
              value={rule.label}
              onChange={(e) => setRow('rules', i, { label: e.target.value })}
              placeholder="Team size"
            />
            <textarea
              rows={2}
              value={rule.text}
              onChange={(e) => setRow('rules', i, { text: e.target.value })}
              placeholder="Teams must have between 2 and 5 students."
            />
            <button type="button" className={styles.dangerBtn} onClick={() => removeRow('rules', i)}>
              Remove
            </button>
          </div>
        ))}
        <button type="button" className={styles.linkBtn} onClick={() => addRow('rules', { label: '', text: '' })}>
          + Add rule
        </button>
      </section>

      {/* ---- Important links ---- */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Important links ({draft.links.length})</h3>
        {draft.links.map((link, i) => (
          <div key={i} className={styles.pair}>
            <input
              className={styles.pairLabel}
              value={link.label}
              onChange={(e) => setRow('links', i, { label: e.target.value })}
              placeholder="Official website"
            />
            <input
              value={link.url}
              onChange={(e) => setRow('links', i, { url: e.target.value })}
              placeholder="https://..."
            />
            <button type="button" className={styles.dangerBtn} onClick={() => removeRow('links', i)}>
              Remove
            </button>
          </div>
        ))}
        <button type="button" className={styles.linkBtn} onClick={() => addRow('links', { label: '', url: '' })}>
          + Add link
        </button>
        <span className={styles.hint}>Links that are not http:// or https:// are dropped.</span>
      </section>

      {/* ---- Submission roadmap ---- */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Submission roadmap</h3>
        {roadmapSlots.map(([slot, label, hint]) => (
          <div key={slot} className={styles.card}>
            <span className={styles.cardTitle}>{label}</span>
            <span className={styles.hint}>{hint}</span>
            <label className={styles.field}>Heading
              <input value={draft.roadmap[slot].title} onChange={setRoadmap(slot, 'title')} />
            </label>
            <label className={styles.field}>Detail
              <textarea rows={2} value={draft.roadmap[slot].text} onChange={setRoadmap(slot, 'text')} />
            </label>
          </div>
        ))}
      </section>

      <div className={styles.actions}>
        <Button variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button onClick={() => onSave(draft)} loading={busy}>Save and publish</Button>
      </div>
    </div>
  );
};

export default CompetitionOverviewEditor;
